import type { UserTask } from '@/api/userTasks/useUserTaskStream'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Button, Progress, Typography, theme } from 'antd'

const { Text } = Typography

interface UserTaskToastProps {
  task: UserTask
  onDismiss: (taskId: string) => void
  dismissing?: boolean
}

export function isTaskActive(task: UserTask): boolean {
  return task.status === 'pending' || task.status === 'running'
}

function hasProgress(task: UserTask): boolean {
  return (
    task.total_count !== null &&
    task.total_count !== undefined &&
    task.total_count > 0
  )
}

function StatusIcon({ task }: { task: UserTask }) {
  const { token } = theme.useToken()
  switch (task.status) {
    case 'complete':
      return <CheckCircleFilled style={{ color: token.colorSuccess }} />
    case 'error':
      return <CloseCircleFilled style={{ color: token.colorError }} />
    default:
      return <LoadingOutlined style={{ color: token.colorPrimary }} />
  }
}

export function UserTaskToast({
  task,
  onDismiss,
  dismissing,
}: UserTaskToastProps) {
  const { token } = theme.useToken()
  const completed = task.completed_count ?? 0
  const total = task.total_count ?? 0
  const percent = hasProgress(task) ? Math.round((completed / total) * 100) : 0

  return (
    <div
      data-testid='user-task-toast'
      style={{
        padding: '10px 12px',
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgElevated,
        boxShadow: token.boxShadowSecondary,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <StatusIcon task={task} />
        <Text strong ellipsis style={{ flex: 1, minWidth: 0 }}>
          {task.title}
        </Text>
        <Button
          type='text'
          size='small'
          aria-label={`Dismiss ${task.title}`}
          icon={<CloseOutlined />}
          loading={dismissing}
          onClick={() => task._id && onDismiss(String(task._id))}
          style={{ flexShrink: 0 }}
        />
      </div>

      <Text type='secondary'>{task.message}</Text>

      {hasProgress(task) && isTaskActive(task) && (
        <Progress
          percent={percent}
          size='small'
          showInfo={false}
          status='active'
          style={{ marginBottom: 0 }}
        />
      )}
    </div>
  )
}
